import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Card, CardContent, Button, CircularProgress } from '@mui/material';
import { getArticles } from '../lib/supabase';
import type { Article } from '../types/database.types';

const ArticlesPage: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'All Publications | Kevyn Franco';
  }, []);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const data = await getArticles();
        setArticles(data);
      } catch (err) {
        console.error('Error fetching articles:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'white',
        }}
      >
        <CircularProgress sx={{ color: '#dc143c' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        pt: 12,
        pb: 8,
      }}
    >
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              color: '#1a1a1a',
              mb: 3,
            }}
          >
            ALL PUBLICATIONS
          </Typography>
          <Typography
            sx={{
              fontSize: '1.25rem',
              color: '#666',
              maxWidth: '800px',
            }}
          >
            Explore my complete collection of articles on AI, software engineering, and technology.
          </Typography>
        </Box>

        {/* Articles Grid */}
        {articles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h3" sx={{ color: '#666', mb: 2 }}>
              No articles found
            </Typography>
            <Typography sx={{ color: '#999' }}>
              Check back soon for new content!
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 4,
            }}
          >
            {articles.map((article) => (
              <Box key={article.id}>
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                    },
                  }}
                  onClick={() => navigate(`/article/${article.slug}`)}
                >
                  {article.image_url && (
                    <Box
                      component="img"
                      src={article.image_url}
                      alt={article.title}
                      sx={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#999',
                        mb: 2,
                      }}
                    >
                      {article.author} • {new Date(article.published_at || '').toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                      {article.reading_time && ` • ${article.reading_time} min read`}
                    </Typography>

                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        fontWeight: 900,
                        color: '#1a1a1a',
                        mb: 2,
                        fontFamily: '"Arial Black", sans-serif',
                        lineHeight: 1.3,
                      }}
                    >
                      {article.title}
                    </Typography>

                    {article.excerpt && (
                      <Typography
                        sx={{
                          fontSize: '1rem',
                          color: '#666',
                          mb: 3,
                          lineHeight: 1.6,
                          flexGrow: 1,
                        }}
                      >
                        {article.excerpt}
                      </Typography>
                    )}

                    {/* Tags */}
                    {article.tags && article.tags.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                        {article.tags.slice(0, 3).map((tag, index) => (
                          <Box
                            key={index}
                            sx={{
                              px: 2,
                              py: 0.5,
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: '#666',
                            }}
                          >
                            {tag}
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Button
                      sx={{
                        color: '#dc143c',
                        fontWeight: 700,
                        textDecoration: 'underline',
                        textUnderlineOffset: '4px',
                        textDecorationThickness: '2px',
                        p: 0,
                        alignSelf: 'flex-start',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                          color: '#b01030',
                        },
                      }}
                    >
                      Read Full Article
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}

        {/* Back Button */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Button
            onClick={() => navigate('/')}
            sx={{
              color: '#1a1a1a',
              fontWeight: 700,
              fontSize: '1.125rem',
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
              textDecorationThickness: '2px',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#dc143c',
              },
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ArticlesPage;